import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  betaAddGmailAccountDelegate,
  betaListGmailAccountDelegates,
  listExpiringGmailAccountDelegates,
} from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import { DelegateModel } from '../../../../../../types';

import DeleteAccountDelegateDialog from './delete-account-delegate-dialog';
import SharedAutocomplete from '../../../../../shared/shared-autocomplete';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import AccountDelegationExpirationChip from './account-delegation-expiration-chip';
import { useParams } from 'react-router-dom';
import { useUserSettings } from '../../../../../utils/use-user-settings';
import { Messages } from '../../../../../utils/message-constants';
import UserAvatar from '../../../../../shared/user-avatar';
import StatusLabel, { StatusType } from '../../../../../shared/status-label';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';

export const VALID_STATUS_TYPES = new Set<StatusType>([
  'ACCEPTED',
  'PENDING',
  'REJECTED',
  'EXPIRED',
]);

const isValidStatusType = (status: string): status is StatusType =>
  VALID_STATUS_TYPES.has(status as StatusType);

export default function AccountDelegation() {
  const { userEmail } = useParams() as { userEmail: string };
  const { userRoles, primaryDomain, changeLogRoles } = useUserSettings();
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const [selectedDelegate, setSelectedDelegate] = useState<DelegateModel>();
  const [openDeleteAccountDelegate, setOpenDeleteAccountDelegate] =
    useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [hasError, setHasError] = useState(false);

  const { data: accountDelegates, isLoading } = useQuery({
    queryKey: [UserKeys.AccountDelegates, userEmail],
    queryFn: () => betaListGmailAccountDelegates(userEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('account delegates'),
    },
  });

  const { data: expiringDelegates, isLoading: isLoadingExpiringDelegates } =
    useQuery({
      queryKey: [UserKeys.ExpiringGmailDelegates, userEmail],
      queryFn: () => listExpiringGmailAccountDelegates(userEmail),
      meta: {
        errorMessage: Messages.Errors.Network.Loading('expiring delegates'),
      },
    });

  const { mutate, isPending: isAdding } = useMutation({
    mutationFn: () =>
      betaAddGmailAccountDelegate(
        userEmail,
        autoCompleteResponse,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Created('Account delegate'),
      });
      queryClient.setQueryData(
        [UserKeys.AccountDelegates, userEmail],
        (old: DelegateModel[] | undefined) => {
          const previousDelegates = Array.isArray(old) ? old : [];
          return [
            ...previousDelegates,
            {
              delegateEmail: autoCompleteResponse,
              verificationStatus: 'accepted',
            } as DelegateModel,
          ];
        }
      );

      setAutoCompleteResponse('');
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Creating('this delegate'),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .create_account_delegate,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.accountDelegation,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (accountDelegatesEmails.includes(autoCompleteResponse)) {
      setHasError(true);
    } else {
      setHasError(false);
      if (
        isAbleToSeeUIElement(
          changeLogRoles,
          UserRoles.AddGmailAccountDelegation.value
        )
      ) {
        setOpenChangeLogDialog(true);
      } else {
        mutate();
      }
    }
  }

  const accountDelegatesResult =
    accountDelegates && Array.isArray(accountDelegates) ? accountDelegates : [];

  const accountDelegatesEmails = accountDelegatesResult.map(
    (delegate) => delegate.delegateEmail
  );

  const canAddDelegate = isAbleToSeeUIElement(
    userRoles,
    UserRoles.AddGmailAccountDelegation.value
  );

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ p: 2, overflowY: 'auto' }}>
        <Stack>
          <Stack gap={2}>
            <Box>
              <Typography variant="body2">
                Add or remove users/groups from {''}
                {primaryDomain} to delegate {userEmail}'s Gmail account. Adding
                an expiration to the delegate will automatically remove the
                delegate after the expiration time.
              </Typography>
            </Box>
            <Tooltip
              arrow
              title={
                !canAddDelegate
                  ? Messages.Errors.Permission.Acting(
                      UserRoles.AddGmailAccountDelegation.name
                    )
                  : ''
              }
            >
              <Stack flexDirection="row" gap={1}>
                <Box sx={{ width: '50%' }}>
                  <SharedAutocomplete
                    sources={['users', 'groups']}
                    autocompleteValue={autoCompleteResponse}
                    setAutoCompleteValue={setAutoCompleteResponse}
                    errors={hasError}
                    helperText={
                      hasError ? 'Delegate is already assigned.' : ' '
                    }
                    disabled={!canAddDelegate}
                  />
                </Box>
                <Box>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={autoCompleteResponse === '' || !canAddDelegate}
                    loading={isAdding}
                    sx={{ height: 40 }}
                  >
                    Add Delegate
                  </Button>
                </Box>
              </Stack>
            </Tooltip>
            <Stack flexDirection="row" gap={2}>
              <Card sx={{ width: '50%' }}>
                {!isLoading && !isLoadingExpiringDelegates ? (
                  <List sx={{ maxHeight: '45vh', overflowY: 'auto', pt: 0 }}>
                    <ListSubheader
                      sx={{
                        bgcolor: (theme) =>
                          theme.palette.mode === 'light'
                            ? theme.palette.grey[100]
                            : theme.palette.grey[900],
                      }}
                    >
                      Delegates
                    </ListSubheader>
                    {accountDelegatesResult.map((delegate) =>
                      (() => {
                        const rawStatus =
                          delegate.verificationStatus?.toUpperCase();
                        const status: StatusType =
                          rawStatus && isValidStatusType(rawStatus)
                            ? rawStatus
                            : 'UNSPECIFIED';
                        return (
                          <Box key={delegate.delegateEmail}>
                            <Stack>
                              <ListItem
                                dense
                                secondaryAction={
                                  <Tooltip arrow title="Delete">
                                    <span>
                                      <IconButton
                                        onClick={() => {
                                          setSelectedDelegate(delegate);
                                          setOpenDeleteAccountDelegate(true);
                                        }}
                                        disabled={
                                          !isAbleToSeeUIElement(
                                            userRoles,
                                            UserRoles
                                              .RemoveGmailAccountDelegation
                                              .value
                                          )
                                        }
                                      >
                                        <Delete />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                }
                              >
                                <ListItemAvatar>
                                  <UserAvatar
                                    userEmail={delegate.delegateEmail}
                                  />
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <Typography variant="body1">
                                        {delegate.delegateEmail}
                                      </Typography>
                                      {status ? (
                                        <StatusLabel status={status} />
                                      ) : null}
                                    </Stack>
                                  }
                                  secondary={
                                    <Typography
                                      component="div"
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      <AccountDelegationExpirationChip
                                        key={delegate.delegateEmail}
                                        userEmail={userEmail}
                                        delegateEmail={delegate.delegateEmail}
                                        currentExpirationTime={
                                          expiringDelegates?.find(
                                            (expiringDelegate) =>
                                              expiringDelegate.delegateEmail ===
                                              delegate.delegateEmail
                                          )?.expirationTime ?? 0
                                        }
                                        changeLogRoles={changeLogRoles}
                                      />
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            </Stack>
                            <Divider />
                          </Box>
                        );
                      })()
                    )}
                  </List>
                ) : (
                  <Stack gap={0.5}>
                    <Skeleton variant="rounded" height={53} />
                    <Skeleton variant="rounded" height={53} />
                    <Skeleton variant="rounded" height={53} />
                    <Skeleton variant="rounded" height={53} />
                  </Stack>
                )}
              </Card>
              <Alert severity="info" sx={{ width: '50%' }}>
                <AlertTitle>Delegate Information</AlertTitle>
                To use this feature Account Delegation must be activated in the{' '}
                <a
                  id="anchor"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={'https://admin.google.com'}
                >
                  google apps cPanel
                </a>
                . If you have not done so already, please activate Account
                Delegation.
                <Divider sx={{ my: 1 }} />
                Gmail imposes limitations depending on your organization, but in
                general each user can have up to 25 delegates and up to 10
                delegators.
                <Divider sx={{ my: 1 }} />
                Delegates:
                <ul>
                  <li>
                    May read, send, and delete emails in {userEmail}'s mailbox.
                  </li>
                  <li>
                    May access {userEmail}'s contacts using the user interface's
                    Contacts link as well as selecting the To, Cc, or Bcc links
                    in the mail compose window.
                  </li>
                  <li>
                    May not change {userEmail}'s account password, settings, or
                    chat as {userEmail}.
                  </li>
                </ul>
                <Divider sx={{ my: 1 }} />
                Please note, it can take up to 24 hours before delegation
                settings are applied in Gmail.
                <Divider sx={{ my: 1 }} />
                If you add delegates from gPanel, they will not show up in
                cPanel logs due to an issue with Google. This will also bypass
                the acceptance flow and will automatically set their status to
                Accepted, meaning they will not be able to decline the
                delegation.
                <Divider sx={{ my: 1 }} />
                For more information, please see{' '}
                <a
                  id="anchor"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={
                    'https://resources.promevo.com/knowledgebase/gpanel/users/profile-information'
                  }
                >
                  gPanel's help documentation
                </a>
                .
              </Alert>
            </Stack>
          </Stack>
          {selectedDelegate ? (
            <DeleteAccountDelegateDialog
              open={openDeleteAccountDelegate}
              handleClose={() => setOpenDeleteAccountDelegate(false)}
              userEmail={userEmail}
              selectedDelegate={selectedDelegate}
              delegateList={accountDelegates}
              changeLogRoles={changeLogRoles}
            />
          ) : null}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isAdding}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
